import validate from 'validate.js';

import { CoreoAttribute, CoreoRecord, CoreoRecordSyncStatus } from '../types';

import { Geometry } from 'geojson';
import { booleanWithin } from '@turf/turf';
import { FormsState } from '../store/forms/forms.reducer';

export type GeometryValidationParams = {
  childSurveysToEnforce: number[]; // Could we store survey title here and add it to the message?
}

type WithinParentOptions = {
  message?: string;
}

type ChildrenWithinOptions = {
  surveyIds: number[];
  message?: string;
}

type ValidationAttributes = {
  value: Geometry;
  record: CoreoRecord;
  forms: FormsState;
}

export const isAreaGeometry = (geometry: Geometry): boolean =>
  geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';

export class ValidationEngine {

  static buildConstraints = (attribute: CoreoAttribute, geometryValidationParams: GeometryValidationParams): any => {

    /** Custom validation for enforceParentBounds */
    validate.validators.withinParent = (
      value: Geometry,
      options: WithinParentOptions,
      _key: string,
      attributes: any
    ) => {
      if (!value) return null;

      const { forms } = attributes as ValidationAttributes;

      // Need at least two forms to have a parent (current form + parent form)
      if (!forms.forms.length || forms.forms.length < 2) return null;

      const parentForm = forms.forms[forms.forms.length - 2];

      const parentGeometry = parentForm?.record?.geometry as Geometry | undefined;

      if (!parentGeometry) return null;

      if (!isAreaGeometry(parentGeometry)) {
        return null;
      }

      const isWithin = booleanWithin(value, parentGeometry);

      if (!isWithin) {
        return options.message || "must be within the parent's geometry";
      }

      return null;
    };

    /** Custom validation for enforceParentBounds on geometry's children */
    validate.validators.childrenWithin = (
      value: Geometry,
      options: ChildrenWithinOptions,
      _key: string,
      attributes: any
    ) => {
      if (!value) return null;

      if (!isAreaGeometry(value)) {
        return null;
      }

      if (!options || !options.surveyIds) {
        throw new Error("children within validator requires a 'surveyIds' option.");
      }

      const { record } = attributes as ValidationAttributes;

      if (!record) return null;

      const { associates } = record;

      const associateRecords = Object.values(associates).flat();

      const geometries = associateRecords
        .filter(r => options.surveyIds.includes(r.surveyId))
        .filter(r => r.syncState !== CoreoRecordSyncStatus.PENDING_DELETE)
        .filter(r => r.geometry != null)
        .map(r => r.geometry);

      if (geometries.length > 0) {
        let isWithin = true;

        for (const g of geometries) {
            const inBounds = booleanWithin(g, value);
            if (!inBounds) {
                isWithin = false;
                break;
            }
          }

          if (!isWithin) {
            return options.message || "must contain children";
          }
      }

      return null;
    };

    const cfg = attribute.config || {};
    const constraints: any = {};

    constraints.presence = attribute.required ? {
      allowEmpty: false
    } : false;

    switch (attribute.questionType) {
      case 'child':
      case 'mediaGallery':
      case 'association': {
        constraints.length = {
          minimum: cfg.min || 0,
          maximum: cfg.max || Number.MAX_SAFE_INTEGER,
          tooShort: 'must have at least %{count}',
          tooLong: 'may have up to %{count}'
        };
        break;
      }
      case 'geometry': {
        if (cfg.hasOwnProperty('enforceParentBounds') && cfg.enforceParentBounds === true) {
          constraints.withinParent = {
            message: 'must be located inside the parent geometry'
          }
        };
        if (!!geometryValidationParams && !!geometryValidationParams.childSurveysToEnforce && geometryValidationParams.childSurveysToEnforce.length > 0) {
          constraints.childrenWithin = {
            surveyIds: geometryValidationParams.childSurveysToEnforce,
            message: "must have children within its geometry"
          }
        };
        break;
      }
    }

    switch (attribute.type) {
      case 'integer': {
        const numericalityConfig: any = {
          onlyInteger: true,
        };
        if (cfg.hasOwnProperty('min') && !isNaN(parseInt(cfg.min, 10))) {
          numericalityConfig.greaterThanOrEqualTo = Number(cfg.min);
        }
        if (cfg.hasOwnProperty('max') && !isNaN(parseInt(cfg.max, 10))) {
          numericalityConfig.lessThanOrEqualTo = Number(cfg.max);
        }
        constraints.numericality = numericalityConfig;
        break;
      }
      case 'float': {
        const numericalityConfig: any = {
          onlyInteger: false,
        };
        if (cfg.hasOwnProperty('min') && !isNaN(parseInt(cfg.min, 10))) {
          numericalityConfig.greaterThanOrEqualTo = Number(cfg.min);
        }
        if (cfg.hasOwnProperty('max') && !isNaN(parseInt(cfg.max, 10))) {
          numericalityConfig.lessThanOrEqualTo = Number(cfg.max);
        }
        constraints.numericality = numericalityConfig;
        break;
      }
      case 'boolean': {
        if (attribute.questionType === 'confirm' && attribute.required) {
          constraints.inclusion = {
            within: [true],
            message: 'must be confirmed to proceed'
          };
          break;
        }
        constraints.inclusion = {
          within: [true, false, 1, 0, 'true', 'false', '1', '0'],
          message: 'must be true, false, 1, 0, "true", "false", "0" or "1"'
        };
        break;
      }
      case 'text': {
        if (cfg.format) {
          constraints.format = {
            pattern: cfg.format,
            flags: 'i'
          };
        }
        break;
      }
      case 'email':
      case 'url': {
        constraints[attribute.type] = true;
        break;
      }
    }
    return constraints;
  };

  static validateAnswer = (attribute: CoreoAttribute, constraints: any, record: CoreoRecord, forms: FormsState): string => {
    // Determine what value to pass to the constraint based on the attribute type
    let value;


    switch (attribute.questionType) {
      case 'geometry': {
        value = record.geometry;
        break;
      }
      case 'child':
      case 'association': {
        value = record.associates[attribute.id] || [];
        break;
      }
      case 'signature':
      case 'photo': {
        value = record.attachments.find(a => a.attributeId === attribute.id);
        break;
      }
      case 'mediaGallery': {
        value = record.attachments.filter(a => a.attributeId === attribute.id);
        break;
      }
      default: {
        value = record && attribute && attribute.path && record.data[attribute.path];
      }
    }

    const result = validate({ value, record, forms }, { value: constraints });
    return result && result.value && result.value[0] || null;
  };
}

export default ValidationEngine;
